import axios from 'axios';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useThrottle } from "@uidotdev/usehooks";
import { isMobileIos } from '../utils/isMobileIos';

const UIContext = React.createContext(null);

const baseApiUrl = import.meta.env.VITE_ENC_API_URL_PREFIX;
const apiToken = import.meta.env.VITE_VECTOR_CHARTS_API_TOKEN;

export default UIContext;

export function UIContextProvider({ children }) {
    const location = useLocation();
    const [searchQuery, setSearchQuery] = useState(null);
    const [searchResults, setSearchResults] = useState(null);
    const [forceReload, setForceReload] = useState(null);
    const [attributionsOpen, setAttributionsOpen] = useState(false);
    const [mobileToolbarOpen, setMobileToolbarOpen] = useState(false);
    const [locationTracking, _setLocationTracking] = useState(false);
    const [trackInternal, setTrack] = useState(null);
    const watchRef = useRef(null);

    const appendTrackPoint = (coords) => {
        setTrack((old) => {
            const latestPoint = {
                position: [coords.latitude, coords.longitude],
                heading: coords.heading,
                altitude: coords.altitude,
                timestamp: new Date().valueOf(),
            };
            const history = old?.history ? [...old.history] : [];
            history.push(latestPoint);

            return {
                startTime: old?.startTime ?? new Date().valueOf(),
                latest: latestPoint,
                history,
            };
        });
    };

    const onPosition = (position) => {
        appendTrackPoint({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            heading: position.coords.heading,
            altitude: position.coords.altitude,
        });
    };

    const onLocationError = (err) => {
        console.log('Failed to start location tracking :(', err);
        _setLocationTracking(false);
    };

    const startLocationWatch = () => {
        if (!navigator.geolocation) {
            console.log('Failed to start location tracking :(', 'Geolocation is not supported in this browser.');
            _setLocationTracking(false);
            return;
        }

        // Must be called synchronously from the user gesture (iOS Safari/Chrome).
        watchRef.current = navigator.geolocation.watchPosition(
            onPosition,
            onLocationError,
            {
                enableHighAccuracy: true,
                maximumAge: 0,
                timeout: 10000,
            }
        );
    };

    const setLocationTracking = (tracking) => {
        if (!tracking) {
            if (watchRef.current != null) {
                navigator.geolocation.clearWatch(watchRef.current);
                watchRef.current = null;
            }
            setTrack(null);
            _setLocationTracking(false);
            return;
        }

        if (watchRef.current != null) {
            navigator.geolocation.clearWatch(watchRef.current);
            watchRef.current = null;
        }

        const startTime = new Date().valueOf();
        setTrack({
            startTime,
            history: [],
            latest: null,
        });
        _setLocationTracking(true);
        startLocationWatch();
    };

    const track = locationTracking ? trackInternal : null;

    const stopWatching = () => {
        if (watchRef.current != null) {
            navigator.geolocation.clearWatch(watchRef.current);
            watchRef.current = null;
        }
    };

    useEffect(() => {
        if (!locationTracking) {
            stopWatching();
            setTrack(null);
        }
    }, [locationTracking]);

    useEffect(() => {
        return () => {
            stopWatching();
        };
    }, []);

    const mobileIos = isMobileIos();
    const rootStyle = getComputedStyle(document.documentElement);
    const insets = {
        top: rootStyle.getPropertyValue('--sat').trim() || '0px',
        bottom: rootStyle.getPropertyValue('--sab').trim() || '0px',
        right: rootStyle.getPropertyValue('--sar').trim() || '0px',
        left: rootStyle.getPropertyValue('--sal').trim() || '0px',
    };

    const searchParams = new URLSearchParams(window.location.search);

    const history = useHistory();

    const queryElevation = async (data) => {
        const result = await axios.post(`${baseApiUrl}/api/v2/query/elevation?token=${apiToken}`, data);
        if (result.status == 200) {
            return result.data;
        }
    };

    const createUserReport = async ({ reportType, position, properties, id, externalUserId }) => {
        const body = {
            reportType,
            position,
        };
        if (properties !== undefined) {
            body.properties = properties;
        }
        if (id !== undefined) {
            body.id = id;
        }
        if (externalUserId !== undefined) {
            body.externalUserId = externalUserId;
        }
        const result = await axios.post(`${baseApiUrl}/api/v1/user-reports?token=${apiToken}`, body);
        if (result.status == 200 || result.status == 201) {
            return result.data;
        }
        throw new Error('Failed to create user report');
    };

    const voteOnUserReport = async (reportId, { vote, externalUserId } = {}) => {
        const body = { vote };
        if (externalUserId !== undefined) {
            body.externalUserId = externalUserId;
        }
        const result = await axios.post(
            `${baseApiUrl}/api/v1/user-reports/${reportId}/votes?token=${apiToken}`,
            body
        );
        if (result.status == 200) {
            return result.data;
        }
        throw new Error('Failed to vote on user report');
    };

    const deleteUserReport = async (reportId) => {
        const result = await axios.delete(
            `${baseApiUrl}/api/v1/user-reports/${reportId}?token=${apiToken}`
        );
        if (result.status == 200) {
            return result.data;
        }
        throw new Error('Failed to delete user report');
    };

    const setBlob = async (data) => {
        const result = await axios.post(`${baseApiUrl}/api/v1/blobs?token=${apiToken}`, data);
        if (result.status == 200) {
            return result.data;
        }
    };
    const getBlob = async (id) => {
        const result = await axios.get(`${baseApiUrl}/api/v1/blobs/${id}?token=${apiToken}`);
        if (result.status == 200) {
            return result?.data?.value || null;
        } else {
            return null;
        }
    };
    const [selectedSearchResult, setSelectedSearchResult] = useState(null);

    const prevPathnameRef = useRef(null);
    useEffect(() => {
        const pathname = location.pathname;
        const prev = prevPathnameRef.current;
        const sidebarClosed = pathname === '/' || pathname === '';
        const wasSidebarOpen = prev != null && prev !== '/' && prev !== '';
        if (sidebarClosed && wasSidebarOpen) {
            setSearchQuery(null);
            setSearchResults(null);
            setSelectedSearchResult(null);
            const params = new URLSearchParams(location.search);
            if (params.has('sid')) {
                params.delete('sid');
                history.replace({ pathname: '/', search: params.toString() });
            }
        }
        prevPathnameRef.current = pathname;
    }, [location.pathname]);

    const getAttributions = async () => {
        const result = await axios.get(`${baseApiUrl}/api/v1/datasets/attributions`);
        if (result.status == 200) {
            return result?.data || null;
        } else {
            return null;
        }
    };

    const makeGetRequest = async (url) => {
        const result = await axios.get(url);
        return result;
    };
    window.makeGetRequest = makeGetRequest;

    useEffect(() => {
        const updateBlobFromUrl = async () => {
            // Load blob initially.
            const params = new URLSearchParams(window.location.search);
            const sid = params.get('sid') || null;
            if (sid) {
                const value = await getBlob(sid);
                setSelectedSearchResult(value);
            }
        };
        updateBlobFromUrl();
    }, [forceReload]);

    const commitSelectedSearchResult = async (data) => {
        setSelectedSearchResult(data || null);
        const params = new URLSearchParams(window.location.search);
        if (data) {
            const { id } = await setBlob(data);
            params.set('sid', id);
        } else {
            params.delete('sid');
        }

        history.replace({ pathname: window.location.pathname, search: params.toString() });   
    };

    const [searchLoading, setSearchLoading] = useState(false);
    const searchInternalState = useRef({
        active: false,
    });
    const startSearch = async () => {
        setSearchLoading(true);
        setSearchResults(null);

        if (!searchQuery.position) {
            setSearchResults({ results: [] });
            setSearchLoading(false);
            return;
        }

        try {
            const searchResult = await axios.post(`${baseApiUrl}/api/v1/enc_query?token=${apiToken}`, searchQuery);
            if (searchResult.status == 200) {
                setSearchResults(searchResult.data);
            }
        } catch (e) {
            setSearchResults({ results: [] });
        } finally {
            setSearchLoading(false);
        }
    };
    useEffect(() => {
        if (searchQuery) {
            startSearch();
        } else {
            setSearchLoading(false);
        }
    }, [searchQuery]);


    const [routeInputWaypoints, setRouteInputWaypoints] = useState([]);
    const [routeResults, setRouteResults] = useState(null);
    const [routeSafetyMargin, setRouteSafetyMargin] = useState(0.5);
    const latestRouteRequest = useRef(new Date().valueOf());

    const throttledRouteInputWaypoints = useThrottle(routeInputWaypoints, 1000);

    const startRouting = async () => {
        // latestRouteRequest.current = new Date().valueOf();
        const thisRequest = new Date().valueOf();

        setRouteResults({
            loading: true
        });

        console.log(`Started routing (${throttledRouteInputWaypoints.length} waypoints)`);
        console.log(throttledRouteInputWaypoints);

        try {
            setRouteResults({
                loading: true
            });
            const routeResult = await axios.post(`${baseApiUrl}/api/v1/routing/route?token=${apiToken}`, {
                waypoints: throttledRouteInputWaypoints.map((pt) => ([pt.latitude, pt.longitude])),
                weights: {
                    safety: routeSafetyMargin,
                }
            });

            if (latestRouteRequest.current > thisRequest) {
                // Preempted by a newer request. Ignore!
                return;
            }
            // console.log(routeResult);
            if (routeResult.status == 200) {
                setRouteResults({
                    ...routeResult.data,
                    loading: false
                });
            } else {
                setRouteResults({
                    loading: false,
                    success: false,
                    messages: [{
                        message: "Failed to route."
                    }],
                    route: []
                });
            }
        } catch (e) {
            // console.log(e);
            setRouteResults({
                loading: false,
                success: false,
                messages: [{
                    message: "Failed to route."
                }],
                route: []
            });
        }
    };

    useEffect(() => {
        latestRouteRequest.current = new Date().valueOf();
        setRouteResults({
            loading: true
        });
    }, [routeInputWaypoints, routeSafetyMargin]);

    useEffect(() => {
        if (throttledRouteInputWaypoints.length > 1) {
            startRouting();
        } else {
            setRouteResults({
                loading: false,
                success: false,
                route: [],
                messages: [{
                    message: 'Not enough points to route. Please add 2 or more points!'
                }]
            });
        }
    }, [throttledRouteInputWaypoints, routeSafetyMargin]);

    return (
        <UIContext.Provider
            value={{
                routeInputWaypoints,
                setRouteInputWaypoints,
                routeResults,
                setRouteResults,
                routeSafetyMargin,
                setRouteSafetyMargin,
                searchQuery,
                setSearchQuery,
                searchResults,
                setSearchResults,
                searchLoading,
                setSearchLoading,
                selectedSearchResult,
                setSelectedSearchResult,
                forceReload,
                setForceReload,
                commitSelectedSearchResult,
                isMobileIos: mobileIos,
                insets,
                mobileToolbarOpen,
                setMobileToolbarOpen,
                locationTracking,
                setLocationTracking,
                track,
                queryElevation,
                attributionsOpen, 
                setAttributionsOpen,
                getAttributions,
                createUserReport,
                voteOnUserReport,
                deleteUserReport,
            }}
        >
            {children}
        </UIContext.Provider>
    );
}
